/**
 * The pool screen. `/pools?poolId=` is no longer a column of cards under the
 * app chrome; it is a screen of its own (ISSUE-952).
 *
 * `POST /pool/get` is the single read that drives it, exactly as the pool
 * home card stack it replaces did: the header, the loading skeletons, the
 * failed retry banner and the four tabs below.
 *
 * A `FORBIDDEN_POOL` read renders the inline no-access state and raises no
 * modal, per the Q1 part B decision from ISSUE-512's open-questions record
 * (dated 2026-08-22). `PoolSettingsPane` uses the identical rule.
 *
 * The tabs are `keepMounted`, which is the web answer to the legacy app's
 * `_KeepAliveTab`: switching tabs does not throw away the tab you left, so a
 * half-filled form or a scroll position is still there on return (R7). Under
 * 640px the tabs share the row in four equal columns with the legacy app's
 * icon above each label (ISSUE-965); at 640px and up they are the text row
 * they were.
 *
 * Every phase carries a way off the screen. The shell draws no header here
 * (at phone width the section bar still sits at the bottom, ISSUE-965), so
 * whatever this pane paints is the whole top of the document: a `loading` or
 * `failed` phase with no back control leaves a fan with a retry banner and no
 * navigation at all, which the browser's own Back button is the only escape
 * from. The ready phase gets the full header row; the rest get the arrow
 * alone, because they have no pool name or role to draw the rest from.
 *
 * The Commissioner and Admins get a Start a contest action on the Game and
 * Results no-contest states and the one New contest dialog mount
 * (ISSUE-1042).
 */
import { useContestList } from "@sportsmet/contest-sdk/private";
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import { Button, InlineBanner, Select, Skeleton, Tabs } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import {
  isPoolNoAccessCode,
  POOL_NO_ACCESS_ACTION_LABEL,
  POOL_NO_ACCESS_BODY,
  POOL_NO_ACCESS_TITLE,
} from "../../errors/poolAccess";
import { useOverlay } from "../../routeState/useOverlay";
import {
  ClipboardCheckIcon,
  GearIcon,
  PersonIcon,
  TrophyIcon,
} from "../../shell/SectionIcon";
import { NewContestDialogContainer } from "../newContest/NewContestDialogContainer";
import { PickemNewContestDialog } from "../pickem/PickemNewContestDialog";
import { NEW_CONTEST_DIALOG_NAME } from "../newContest/newContestModel";
import { LeavePoolButton } from "../poolHome/LeavePoolButton";
import {
  POOL_HOME_FAILED_MESSAGE,
  POOL_HOME_LOADING_LABEL,
  POOL_HOME_RETRY_LABEL,
  resolvePoolHomeState,
} from "../poolHome/poolHomeState";
import { PoolMembersPane } from "../poolMembers/PoolMembersPane";
import { contestSelectorLabel } from "../poolSettings/contestsCardModel";
import { PoolStandingsPane } from "../poolStandings/PoolStandingsPane";
import { PoolGameTabPane } from "./PoolGameTabPane";
import {
  POOL_TAB_GAME_LABEL,
  POOL_TAB_MEMBERS_LABEL,
  POOL_TAB_RESULTS_LABEL,
  POOL_TAB_SETTINGS_LABEL,
  POOL_TABS_LABEL,
} from "./poolScreenCopy";
import { PoolScreenBackButton, PoolScreenHeader } from "./PoolScreenHeader";
import type { typePoolTab } from "./poolScreenRoute";
import { PoolSettingsTabPane } from "./PoolSettingsTabPane";
import { poolTypePanes } from "./poolTypePanes";

export type typePoolScreenPaneProps = {
  poolId: string;
  tab: typePoolTab;
  /** The open tab's own sub-address, from `?panel=`. */
  panel: string | null;
  memberQ: string;
  contestId?: string | undefined;
  onTabChange: (tab: typePoolTab) => void;
  onGoBack: () => void;
  onSearchQueryChange: (next: string) => void;
  onOpenMemberDetail: (userId: string) => void;
  onOpenChat: (chatId: string) => void;
  onClosePanel: () => void;
};

export const PoolScreenPane = ({
  poolId,
  tab,
  panel,
  memberQ,
  contestId,
  onTabChange,
  onGoBack,
  onSearchQueryChange,
  onOpenMemberDetail,
  onOpenChat,
  onClosePanel,
}: typePoolScreenPaneProps): ReactNode => {
  const pool = usePoolGet({ poolId });
  const navigate = useNavigate();
  const overlay = useOverlay();
  const contestList = useContestList(
    { poolId },
    {
      enabled:
        !pool.isError &&
        !pool.data?.error &&
        pool.data?.data?.pool.contestType === "survivor",
    },
  );
  const contestItems = contestList.data?.data?.items ?? [];
  const selectedContestId = contestItems.some(
    (item) => item.contestId === contestId,
  )
    ? contestId
    : (contestItems.find(
        (item) => item.contestId === contestList.data?.data?.viewerContestId,
      )?.contestId ?? contestItems[0]?.contestId);

  useEffect(() => {
    const code = extractErrorCode(pool.data);
    if (code !== null && !isPoolNoAccessCode(code)) {
      showApiError(pool.data, undefined, pool.dataUpdatedAt);
    }
  }, [pool.dataUpdatedAt, poolId]);

  const state = resolvePoolHomeState({
    isLoading: pool.isLoading,
    hasFailed: pool.isError || Boolean(pool.data?.error),
    errorCode: extractErrorCode(pool.data),
    pool: pool.data?.data?.pool ?? null,
    viewerPoolRole: pool.data?.data?.viewerPoolRole ?? null,
    activeMemberCount: pool.data?.data?.activeMemberCount ?? 0,
    pendingMemberCount: pool.data?.data?.pendingMemberCount ?? 0,
    statusLabel: pool.data?.data?.statusLabel,
  });

  // ISSUE-1334. The pool type picks the Game, Results and Settings panes.
  const contestType = pool.data?.data?.pool.contestType ?? null;
  const typePanes = poolTypePanes(contestType);

  // Presentation only: the server still refuses a member with FORBIDDEN_ROLE.
  const canStartContest =
    state.phase === "ready" &&
    (state.viewerPoolRole === "owner" || state.viewerPoolRole === "admin");
  const openNewContest = canStartContest
    ? () => {
        overlay.open({ kind: "dialog", name: NEW_CONTEST_DIALOG_NAME });
      }
    : undefined;

  const contestSelector =
    typePanes === null && contestItems.length > 0 ? (
      <Select
        label="Contest"
        hint="Choose a contest to see its game, rules and results."
        value={selectedContestId}
        items={contestItems.map((item) => ({
          value: item.contestId,
          label: contestSelectorLabel(item),
        }))}
        onValueChange={(next) => {
          void navigate({
            to: ".",
            search: (previous) => ({
              ...previous,
              contestId: next,
              roundId: undefined,
              entryId: undefined,
            }),
            replace: true,
          });
        }}
      />
    ) : null;

  return (
    <div className="flex flex-col gap-3 pb-10" data-testid="pool-screen-pane">
      {state.phase === "ready" ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <PoolScreenBackButton onGoBack={onGoBack} />
        </div>
      )}
      {state.phase === "loading" ? (
        <>
          <Skeleton shape="title" label={POOL_HOME_LOADING_LABEL} />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
        </>
      ) : null}
      {state.phase === "noAccess" ? (
        <div data-testid="pool-home-no-access">
          <p className="text-body text-ink">{POOL_NO_ACCESS_TITLE}</p>
          <p className="text-body text-ink-secondary">{POOL_NO_ACCESS_BODY}</p>
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              void navigate({ to: "/pools", search: {}, replace: true });
            }}
          >
            {POOL_NO_ACCESS_ACTION_LABEL}
          </Button>
        </div>
      ) : null}
      {state.phase === "failed" ? (
        <div data-testid="pool-home-failed">
          <InlineBanner
            variant="warning"
            message={POOL_HOME_FAILED_MESSAGE}
            action={{
              label: POOL_HOME_RETRY_LABEL,
              onAction: () => {
                void pool.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {state.phase === "ready" ? (
        <>
          <PoolScreenHeader
            poolId={poolId}
            poolName={state.header.name}
            viewerPoolRole={state.viewerPoolRole}
            onGoBack={onGoBack}
          />
          {/* ISSUE-1280. Where play stands, for every pool type. */}
          {state.statusLine !== null ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="pool-screen-status"
            >
              {state.statusLine}
            </p>
          ) : null}
          <Tabs
            keepMounted
            fillOnPhone
            label={POOL_TABS_LABEL}
            value={tab}
            onValueChange={(next) => {
              onTabChange(next as typePoolTab);
            }}
            items={[
              {
                value: "members",
                label: POOL_TAB_MEMBERS_LABEL,
                icon: <PersonIcon className="size-icon-md shrink-0" />,
                content: (
                  <div className="flex flex-col gap-3">
                    {/* No `onGoBack`: this pane is a tab now, not a
                        destination reached from a pool home, and its own
                        "Back to pool" control would leave the pool it names.
                        The header's arrow above is the one way out. */}
                    <PoolMembersPane
                      poolId={poolId}
                      searchQuery={memberQ}
                      onSearchQueryChange={onSearchQueryChange}
                      onOpenMemberDetail={onOpenMemberDetail}
                    />
                    <LeavePoolButton
                      poolId={poolId}
                      canLeave={state.canLeave}
                    />
                  </div>
                ),
              },
              {
                value: "settings",
                label: POOL_TAB_SETTINGS_LABEL,
                icon: <GearIcon className="size-icon-md shrink-0" />,
                content: (
                  <div className="flex flex-col gap-3">
                    {tab === "settings" ? contestSelector : null}
                    <PoolSettingsTabPane
                      contestType={contestType}
                      poolId={poolId}
                      contestId={selectedContestId}
                      viewerPoolRole={state.viewerPoolRole}
                      chatEnabled={state.chatEnabled}
                      activePanel={panel}
                      onOpenChat={onOpenChat}
                      onClosePanel={onClosePanel}
                    />
                  </div>
                ),
              },
              {
                value: "game",
                label: POOL_TAB_GAME_LABEL,
                icon: <TrophyIcon className="size-icon-md shrink-0" />,
                content: (
                  <div className="flex flex-col gap-3">
                    {tab === "game" ? contestSelector : null}
                    <PoolGameTabPane
                      contestType={contestType}
                      viewerPoolRole={state.viewerPoolRole}
                      canManage={canStartContest}
                      poolId={poolId}
                      contestId={selectedContestId}
                      onStartContest={openNewContest}
                    />
                  </div>
                ),
              },
              {
                value: "results",
                label: POOL_TAB_RESULTS_LABEL,
                icon: <ClipboardCheckIcon className="size-icon-md shrink-0" />,
                content:
                  typePanes !== null ? (
                    <typePanes.ResultsTab
                      poolId={poolId}
                      viewerPoolRole={state.viewerPoolRole}
                    />
                  ) : (
                    <PoolStandingsPane
                      showContestSelector={tab === "results"}
                      isCommissioner={state.viewerPoolRole === "owner"}
                      poolId={poolId}
                      contestId={selectedContestId}
                      panel={tab === "results" ? panel : null}
                      canNudge={canStartContest}
                      onStartContest={openNewContest}
                    />
                  ),
              },
            ]}
          />
          {canStartContest && typePanes === null ? (
            <NewContestDialogContainer poolId={poolId} />
          ) : null}
          {/* ISSUE-1429. A Pick'em pool's next contest, same dialog name. */}
          {canStartContest && contestType === "pickem" ? (
            <PickemNewContestDialog
              poolId={poolId}
              leagueKey={pool.data?.data?.pool.leagueKey ?? ""}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};
